/**
 * Per-tenant marketing tag injection (1NOW-1556).
 *
 * Every tag a rental company wants on its renter site is CONFIG, held on
 * `CompanyWebsiteDetail` and edited in FMS → Website Settings. Nothing here
 * knows about any specific company.
 *
 * Historically only a plain GA id had a config home, so five companies had
 * their tags hardcoded as an `if (company_id === …)` chain in `src/index.tsx`.
 * That chain is gone, and so is the temporary fallback that carried its five
 * tenants until each was configured in FMS (1NOW-1556, step 2).
 *
 * Injection is idempotent: every element carries a stable id, and re-running
 * with the same config is a no-op. Re-running with DIFFERENT config replaces
 * the previous tags, which matters because the settings fetch that drives this
 * can fire more than once in a session.
 */

/** A free-form snippet an operator pasted in (chat widget, heatmap, pixel). */
export interface CustomScript {
  id?: string;
  name?: string;
  /** Raw markup, injected verbatim. See the security note on `injectCustomScripts`. */
  code?: string;
  enabled?: boolean;
}

export interface MarketingTags {
  /** Primary Google tag id. gtag.js serves G-, AW- and GTM- from one loader. */
  googleTagId?: string | null;
  /** Optional second Google tag, for Ads conversions alongside analytics. */
  googleAdsId?: string | null;
  customScripts?: CustomScript[] | null;
}

const GTAG_LOADER_ID = "gtag-script";
const GTAG_INIT_ID = "gtag-init";
const CUSTOM_SCRIPT_ATTR = "data-tenant-script";

const clean = (v?: string | null) => (typeof v === "string" ? v.trim() : "");

/**
 * Load gtag.js once and `config` every supplied id against it. Google's loader
 * is shared across products, so two ids = one script + two config calls (NOT
 * two loaders, which would double-count).
 */
const injectGoogleTags = (ids: string[]) => {
  document.getElementById(GTAG_LOADER_ID)?.remove();
  document.getElementById(GTAG_INIT_ID)?.remove();
  if (!ids.length) return;

  const loader = document.createElement("script");
  loader.id = GTAG_LOADER_ID;
  loader.async = true;
  // The loader is keyed on the first id; the rest are configured below. Which
  // id sits in the URL doesn't affect what gets tracked.
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    ids[0]
  )}`;
  document.head.appendChild(loader);

  const init = document.createElement("script");
  init.id = GTAG_INIT_ID;
  init.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    ${ids.map((id) => `gtag('config', ${JSON.stringify(id)});`).join("\n    ")}
  `;
  document.head.appendChild(init);
};

/**
 * A <script> element created by innerHTML is inert — the HTML spec marks
 * parser-inserted scripts from innerHTML as already-started so they never
 * execute. To actually run a pasted snippet we have to re-create each script
 * node and copy its attributes/body across.
 */
const reviveScript = (source: HTMLScriptElement): HTMLScriptElement => {
  const script = document.createElement("script");
  Array.from(source.attributes).forEach((attr) =>
    script.setAttribute(attr.name, attr.value)
  );
  script.text = source.text;
  return script;
};

/**
 * Inject operator-pasted snippets.
 *
 * SECURITY: `code` is arbitrary markup authored in the admin dashboard and
 * executed here on the public renter site. That is deliberate (operators need
 * to add their own chat/analytics vendors) and is the same capability a Google
 * Tag Manager container already grants. It stays tolerable only because the
 * value is per-company and reaches ONLY that company's own storefront — never
 * another tenant's site and never an FMS/admin page. Don't reuse this helper
 * anywhere that inverts those properties.
 */
const injectCustomScripts = (scripts: CustomScript[]) => {
  const previous = document.head.querySelectorAll(`[${CUSTOM_SCRIPT_ATTR}]`);
  const signature = JSON.stringify(scripts.map((s) => s.code));
  // Most vendor snippets bootstrap themselves on load and can't be torn down
  // by removing the tag, so re-injecting an unchanged set would double-load
  // the widget. Only rebuild when the set actually changed.
  if (previous.length && document.head.dataset.tenantScriptSig === signature) {
    return;
  }
  previous.forEach((node) => node.remove());
  document.head.dataset.tenantScriptSig = signature;
  if (!scripts.length) return;

  scripts.forEach((entry, index) => {
    const code = clean(entry.code);
    if (!code) return;
    const id = String(entry.id || index + 1);

    // Parse in an inert document so nothing loads or executes while we sort
    // the nodes out — `<template>` content is exactly that.
    const holder = document.createElement("template");
    holder.innerHTML = code;

    Array.from(holder.content.childNodes).forEach((node) => {
      const el =
        node.nodeName === "SCRIPT"
          ? reviveScript(node as HTMLScriptElement)
          : (node as HTMLElement);
      if (el.nodeType !== Node.ELEMENT_NODE) return;
      el.setAttribute(CUSTOM_SCRIPT_ATTR, id);
      document.head.appendChild(el);
    });
  });
};

/**
 * Apply a tenant's configured tags to the document. Safe to call repeatedly.
 */
export const applyMarketingTags = (tags: MarketingTags): void => {
  const googleIds = [clean(tags.googleTagId), clean(tags.googleAdsId)].filter(
    (id, i, all) => id && all.indexOf(id) === i
  );
  injectGoogleTags(googleIds);

  const scripts = (tags.customScripts || []).filter(
    (s) => s && s.enabled !== false && clean(s.code)
  );
  injectCustomScripts(scripts);
};
